import { el, clear, fmtInt, fmtDate } from '../ui/dom';
import { renderShell, renderError, renderLoading } from '../ui/shell';
import { caption } from '../ui/components';
import { navigate } from '../router';
import type { RouteContext } from '../router';
import { UnauthorizedError } from '../api/client';
import {
  listConfigVersions,
  getConfigVersion,
  createConfigVersion,
  updateConfigVersion,
  activateConfigVersion,
  cloneConfigVersion,
  deleteConfigVersion,
} from '../api/config';
import type {
  RemoteConfigVersion,
  ConfigVersionInput,
  ConfigMessage,
  ConfigListResult,
} from '../api/config';
import {
  duoSwitchPayload,
  duoSwitchValues,
  navContinuitySwitchPayload,
  navContinuitySwitchValues,
} from './remote-config-support';

const PAGE_SIZE = 20;

class MessagesJsonError extends Error {}

/** Generic `.filter()` predicate that drops null/undefined/false entries. */
function present<T>(value: T | null | undefined | false): value is T {
  return !!value;
}

export async function renderRemoteConfig({ root }: RouteContext): Promise<void> {
  const content = renderShell(root, 'config');

  content.append(
    el('div', { class: 'page-head' }, [el('h1', {}, ['App Config'])]),
    caption(
      'Feature flags, maintenance mode, and update gates served to the Check ID app via ' +
        'GET /config/latest. Edits here only reach the app once a version is Activated.',
    ),
  );

  // Banners live outside the reloadable body so a success/error message from
  // an action (save, activate, clone, delete) survives the reload that follows it.
  const errorBanner = el('div', { class: 'banner banner-error', role: 'alert', hidden: true });
  const successBanner = el('div', {
    class: 'banner',
    style: 'background:var(--ok-bg);color:var(--ok-fg);',
    role: 'status',
    hidden: true,
  });
  content.append(errorBanner, successBanner);

  const body = el('div', { class: 'stack-gap' });
  content.append(body);

  function showError(message: string): void {
    successBanner.hidden = true;
    errorBanner.textContent = message;
    errorBanner.hidden = false;
  }
  function showSuccess(message: string): void {
    errorBanner.hidden = true;
    successBanner.textContent = message;
    successBanner.hidden = false;
  }
  function clearBanners(): void {
    errorBanner.hidden = true;
    successBanner.hidden = true;
  }

  const state: { page: number; selectedId: string | null; isNew: boolean } = {
    page: 1,
    selectedId: null,
    isNew: false,
  };
  let lastList: ConfigListResult | null = null;

  async function load(): Promise<void> {
    renderLoading(body, 'Loading configuration versions…');
    try {
      const list = await listConfigVersions(state.page, PAGE_SIZE);
      lastList = list;
      await renderWithList(list);
    } catch (err) {
      if (err instanceof UnauthorizedError) return navigate('/login');
      renderError(body, err instanceof Error ? err.message : 'Failed to load configuration.');
    }
  }

  async function renderWithList(list: ConfigListResult): Promise<void> {
    let selected: RemoteConfigVersion | null = null;
    if (!state.isNew && state.selectedId) {
      selected = list.items.find((c) => c.id === state.selectedId) ?? null;
      if (!selected) {
        const id = state.selectedId;
        try {
          selected = await getConfigVersion(id);
        } catch {
          selected = null;
        }
      }
    }
    renderContent(list, selected);
  }

  function renderContent(
    list: ConfigListResult,
    selected: RemoteConfigVersion | null,
  ): void {
    clear(body);

    body.append(
      versionsPanel(list, state.selectedId, {
        onSelect: (id) => {
          state.selectedId = id;
          state.isNew = false;
          clearBanners();
          void load();
        },
        onNewConfig: () => {
          state.selectedId = null;
          state.isNew = true;
          clearBanners();
          if (lastList) void renderWithList(lastList);
          else void load();
        },
        onClone: (id) => void handleClone(id),
        onActivate: (v) => void handleActivate(v),
        onDelete: (v) => void handleDelete(v),
        onPage: (page) => {
          state.page = page;
          void load();
        },
      }),
      editorPanel(selected, state.isNew, {
        onSave: (dto, mode) => void handleSave(dto, mode),
        onCancel: () => {
          state.selectedId = null;
          state.isNew = false;
          clearBanners();
          if (lastList) void renderWithList(lastList);
          else void load();
        },
      }),
    );
  }

  async function handleSave(dto: ConfigVersionInput, mode: 'update' | 'create'): Promise<void> {
    clearBanners();
    try {
      const result =
        mode === 'update' && state.selectedId
          ? await updateConfigVersion(state.selectedId, dto)
          : await createConfigVersion(dto);
      state.selectedId = result.id;
      state.isNew = false;
      state.page = 1;
      showSuccess(
        mode === 'update'
          ? `Config v${result.version} updated.`
          : `Config v${result.version} created. Activate it to make it live.`,
      );
      await load();
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to save configuration.');
    }
  }

  async function handleClone(id: string): Promise<void> {
    clearBanners();
    try {
      const cloned = await cloneConfigVersion(id);
      state.selectedId = cloned.id;
      state.isNew = false;
      state.page = 1;
      showSuccess(`Cloned into v${cloned.version}.`);
      await load();
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to clone configuration.');
    }
  }

  async function handleActivate(v: RemoteConfigVersion): Promise<void> {
    if (!window.confirm(`Activate config v${v.version}? This immediately affects all app users.`)) {
      return;
    }
    clearBanners();
    try {
      const result = await activateConfigVersion(v.id);
      showSuccess(`Config v${result.version} is now active.`);
      await load();
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to activate configuration.');
    }
  }

  async function handleDelete(v: RemoteConfigVersion): Promise<void> {
    if (!window.confirm(`Delete config v${v.version}? This cannot be undone.`)) return;
    clearBanners();
    try {
      await deleteConfigVersion(v.id);
      if (state.selectedId === v.id) {
        state.selectedId = null;
        state.isNew = false;
      }
      showSuccess(`Config v${v.version} deleted.`);
      await load();
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to delete configuration.');
    }
  }

  await load();
}

// ── Versions list panel ──────────────────────────────────────────────

function versionsPanel(
  list: ConfigListResult,
  selectedId: string | null,
  actions: {
    onSelect: (id: string) => void;
    onNewConfig: () => void;
    onClone: (id: string) => void;
    onActivate: (v: RemoteConfigVersion) => void;
    onDelete: (v: RemoteConfigVersion) => void;
    onPage: (page: number) => void;
  },
): HTMLElement {
  const newBtn = el(
    'button',
    { class: 'btn btn-primary btn-sm', type: 'button', onClick: actions.onNewConfig },
    ['+ New version'],
  );

  const table =
    list.items.length === 0
      ? el('div', { class: 'empty' }, ['No configuration versions yet.'])
      : el('div', { class: 'table-wrap' }, [
          el('table', { class: 'data-table' }, [
            el(
              'thead',
              {},
              [el('tr', {}, ['Version', 'Status', 'Created', 'Updated', 'Notes', ''].map((h) =>
                el('th', { textContent: h }),
              ))],
            ),
            el(
              'tbody',
              {},
              list.items.map((v) => versionRow(v, v.id === selectedId, actions)),
            ),
          ]),
        ]);

  const pagination =
    list.meta.pages > 1
      ? el('div', { class: 'pagination' }, [
          el(
            'button',
            {
              class: 'btn btn-ghost',
              disabled: list.meta.page <= 1,
              onClick: () => actions.onPage(list.meta.page - 1),
            },
            ['‹ Prev'],
          ),
          el('span', {
            class: 'cell-muted',
            textContent: `Page ${list.meta.page} of ${list.meta.pages}`,
          }),
          el(
            'button',
            {
              class: 'btn btn-ghost',
              disabled: list.meta.page >= list.meta.pages,
              onClick: () => actions.onPage(list.meta.page + 1),
            },
            ['Next ›'],
          ),
        ])
      : null;

  return el('section', { class: 'panel panel-wide' }, [
    el('div', { class: 'panel-head' }, [
      el('div', {}, [
        el('h2', { class: 'panel-title' }, ['Configuration versions']),
        el('p', {
          class: 'panel-sub',
          textContent: `${fmtInt(list.meta.total)} version${list.meta.total === 1 ? '' : 's'}`,
        }),
      ]),
      el('div', { class: 'panel-actions' }, [newBtn]),
    ]),
    el('div', { class: 'panel-body' }, [table, pagination].filter(present)),
  ]);
}

function versionRow(
  v: RemoteConfigVersion,
  isSelected: boolean,
  actions: {
    onSelect: (id: string) => void;
    onClone: (id: string) => void;
    onActivate: (v: RemoteConfigVersion) => void;
    onDelete: (v: RemoteConfigVersion) => void;
  },
): HTMLElement {
  const cloneBtn = el(
    'button',
    {
      class: 'btn btn-ghost btn-sm',
      type: 'button',
      onClick: (ev: MouseEvent) => {
        ev.stopPropagation();
        actions.onClone(v.id);
      },
    },
    ['Clone'],
  );
  const activateBtn = v.isActive
    ? null
    : el(
        'button',
        {
          class: 'btn btn-ghost btn-sm',
          type: 'button',
          onClick: (ev: MouseEvent) => {
            ev.stopPropagation();
            actions.onActivate(v);
          },
        },
        ['Activate'],
      );
  const deleteBtn = v.isActive
    ? null
    : el(
        'button',
        {
          class: 'btn btn-ghost btn-sm btn-danger',
          type: 'button',
          onClick: (ev: MouseEvent) => {
            ev.stopPropagation();
            actions.onDelete(v);
          },
        },
        ['Delete'],
      );

  return el(
    'tr',
    {
      class: 'row-clickable',
      style: isSelected ? 'background:var(--bg);' : '',
      onClick: () => actions.onSelect(v.id),
    },
    [
      el('td', { class: 'cell-strong', textContent: `v${v.version}` }),
      el('td', {}, [
        el('span', {
          class: v.isActive ? 'pill pill-ok' : 'pill',
          textContent: v.isActive ? 'Active' : 'Inactive',
        }),
      ]),
      el('td', { class: 'cell-muted', textContent: fmtDate(v.createdAt) }),
      el('td', { class: 'cell-muted', textContent: fmtDate(v.updatedAt) }),
      el('td', { class: 'cell-muted', textContent: v.notes || '—' }),
      el('td', { class: 'num' }, [
        el(
          'div',
          { class: 'button-row' },
          [cloneBtn, activateBtn, deleteBtn].filter(present),
        ),
      ]),
    ],
  );
}

// ── Editor panel ──────────────────────────────────────────────────────

function editorPanel(
  selected: RemoteConfigVersion | null,
  isNew: boolean,
  handlers: {
    onSave: (dto: ConfigVersionInput, mode: 'update' | 'create') => void;
    onCancel: () => void;
  },
): HTMLElement {
  if (!selected && !isNew) {
    return el('section', { class: 'panel panel-wide' }, [
      el('div', { class: 'panel-head' }, [
        el('div', {}, [el('h2', { class: 'panel-title' }, ['Editor'])]),
      ]),
      el('div', { class: 'panel-body' }, [
        el('div', { class: 'empty' }, ['Select a version from the list, or start a new one.']),
      ]),
    ]);
  }

  // Read straight off `selected` (with literal fallbacks for a blank/new
  // form) rather than unifying it with ConfigVersionInput — the two types
  // disagree on nullability for a couple of fields (e.g. `messages`).

  // ── Feature flags ──
  const checkIDPro = checkboxField(
    'Check ID Pro',
    selected ? selected.checkIDPro : false,
    'Enable Pro features globally',
  );
  const useCheckIDVerifierToken = checkboxField(
    'Verifier Token',
    selected ? selected.useCheckIDVerifierToken : true,
    'Use the Check ID verifier token for validation',
  );
  const useMDLDataRequestEnabled = checkboxField(
    'mDL Data Request (legacy, pre-3.0 — leave OFF)',
    selected ? selected.useMDLDataRequestEnabled === true : false,
    'Pre-3.0 builds request an Apple Verifier element (documentDHSComplianceStatus) this App ID has not been granted. Retained as the permanent off switch for those builds — do not enable.',
  );
  const mdlDataRequestGrantedElementsEnabled = checkboxField(
    'mDL Data Request (3.0+, granted elements)',
    selected ? selected.mdlDataRequestGrantedElementsEnabled === true : false,
    'Read REAL ID status, expiry and issuing authority from Apple Wallet IDs (off = display-only sheet). Only 3.0+ clients read this flag.',
  );
  const passportScanEnabled = checkboxField(
    'Passport Scan',
    selected ? selected.passportScanEnabled : true,
    'Enable NFC passport chip scanning',
  );
  const mobileIDEnabled = checkboxField(
    'Mobile ID',
    selected ? selected.mobileIDEnabled : true,
    "Enable mobile driver's license scanning",
  );
  const scanHistoryEnabled = checkboxField(
    'Scan History',
    selected ? selected.scanHistoryEnabled : true,
    'Allow users to view past scans',
  );
  // Three separate tills, deliberately. Teams can be on sale in the app, on
  // the web, both, or neither, and each answers to a different authority — so
  // one switch would force decisions that have nothing to do with each other.
  const businessSubscriptionEnabled = checkboxField(
    'Teams: in-app purchase',
    selected ? selected.businessSubscriptionEnabled : true,
    'Sells Check ID for Teams through the App Store. Must be ON before submitting a build, or App Review sees "Coming soon" and cannot test the subscription.',
  );
  const teamsWebPurchaseEnabled = checkboxField(
    'Teams: web checkout',
    selected ? selected.teamsWebPurchaseEnabled : false,
    'Sells Check ID for Teams through Stripe on the console. Enforced server-side, so this one cannot be bypassed by a stale client.',
  );
  const teamsWebPurchaseLinkEnabled = checkboxField(
    'Teams: web link inside the app (US only)',
    selected ? selected.teamsWebPurchaseLinkEnabled : false,
    'Shows "subscribe on our website" on the app\u2019s Teams screen, beside the buy button and never instead of it. Permitted only on the US storefront (guideline 3.1.1(a)); the app checks the storefront and the in-app till separately, so this switch alone cannot show it in the wrong place. No commission is being charged on US link-outs today \u2014 Apple\u2019s proposed 5\u201315% schedule was filed 13 Aug 2026 and is not approved. Revisit when it is.',
  );
  const appleSandboxPurchasesOpen = checkboxField(
    'Teams: App Store sandbox purchases',
    selected?.appleSandboxPurchasesOpen ?? true,
    'On: a sandbox purchase (TestFlight, App Review) can activate Teams for any signed-in account. App Review buys in the sandbox with an account of its own, so keep this on while a build is in review. Off: only the App Review account and the named sandbox testers can.',
  );
  // iPhone Duo kill switches. A row that does not say shows ON (never off), so
  // saving an old row, or creating a new version, cannot switch them off.
  const duoShown = duoSwitchValues(selected);
  const duoCameraDirectionEnabled = checkboxField(
    'iPhone Duo: choose camera by direction',
    duoShown.duoCameraDirectionEnabled,
    'Kill switch for new iPhone Duo behaviour: picking the scanning camera by the direction it faces. Leave ON unless it misbehaves; a version that does not say is treated as ON.',
  );
  const duoOuterDisplayEnabled = checkboxField(
    'iPhone Duo: outer display while scanning',
    duoShown.duoOuterDisplayEnabled,
    'Kill switch for new iPhone Duo behaviour: the outer display while scanning. Leave ON unless it misbehaves; a version that does not say is treated as ON.',
  );
  // iOS 27 navigation kill switch. Same rule: a row that does not say shows ON,
  // so saving an old row, or creating a new version, cannot switch it off.
  const navContinuityEnabled = checkboxField(
    'iOS 27 navigation (one split view)',
    navContinuitySwitchValues(selected).navContinuityEnabled,
    'Kill switch for the iOS 27 navigation that keeps one split view across a fold or rotation. Leave ON unless it misbehaves; a version that does not say is treated as ON. Off sends iOS 27 back to the previous navigation. A phone picks the change up on its first cold launch after it has fetched the config.',
  );
  const analyticsEnabled = checkboxField(
    'Analytics',
    selected ? selected.analyticsEnabled : true,
    'Collect usage analytics',
  );
  const showOnboardingEnabled = checkboxField(
    'Welcome Screen',
    selected ? selected.showOnboardingEnabled : true,
    'Show onboarding to new users',
  );

  // ── Limits ──
  const maxFreeDailyScans = numberInput(
    selected ? selected.maxFreeDailyScans : null,
    'Leave blank for server default',
  );
  const maxFreeGuests = numberInput(
    selected ? selected.maxFreeGuests : null,
    'Leave blank for server default',
  );

  // ── Passport certificate list ──
  const certVersion = textInput(selected ? selected.passportCertListVersion : null, '2026.10.01');
  const certURL = textInput(
    selected ? selected.passportCertListURL : null,
    'https://checkid-assets.sfo3.cdn.digitaloceanspaces.com/resources/passportCertificateLists/passportCertificateList-2026.10.01.pem',
  );
  const certSHA256 = textInput(
    selected ? selected.passportCertListSHA256 : null,
    'sha256 hex digest',
  );
  certSHA256.style.fontFamily = 'ui-monospace, SFMono-Regular, Menlo, monospace';
  const certSizeBytes = numberInput(selected ? selected.passportCertListSizeBytes : null, '1258291');
  const certPublishedAt = dateInput(selected ? selected.passportCertListPublishedAt : null);
  const certReleaseNotes = textArea(
    selected ? selected.passportCertListReleaseNotes : null,
    2,
    'Added new CSCA certificates for Germany, France, and Japan',
  );

  // ── Maintenance ──
  const maintenanceEnabled = checkboxField(
    'Maintenance mode',
    selected ? selected.maintenanceEnabled : false,
    'Blocks all app users from using the app while enabled.',
  );
  const maintenanceMessage = textArea(
    selected ? selected.maintenanceMessage : null,
    3,
    "We're performing scheduled maintenance. Please check back soon.",
  );
  const maintenanceEndTime = textInput(
    selected ? selected.maintenanceEndTime : null,
    '2026-01-20T14:00:00Z',
  );
  const maintenanceStatusURL = textInput(
    selected ? selected.maintenanceStatusURL : null,
    'https://status.checkidapp.com',
  );

  // ── Update gates ──
  const minimumVersionRequired = textInput(
    selected ? selected.minimumVersionRequired : null,
    '2.0.0',
  );
  const minimumVersionRequiredMessage = textArea(
    selected ? selected.minimumVersionRequiredMessage : null,
    2,
    'This version of Check ID is no longer supported. Please update to continue.',
  );
  const minimumVersionRecommended = textInput(
    selected ? selected.minimumVersionRecommended : null,
    '2.5.0',
  );
  const minimumVersionRecommendedMessage = textArea(
    selected ? selected.minimumVersionRecommendedMessage : null,
    2,
    'A new version of Check ID is available with improvements.',
  );
  const appStoreURL = textInput(
    selected ? selected.appStoreURL : null,
    'https://apps.apple.com/app/check-id/id733449447',
  );
  const surveyURL = textInput(selected ? selected.surveyURL : null, 'https://forms.example.com/survey');

  // ── Announcements ──
  const messagesJson = textArea(
    JSON.stringify((selected ? selected.messages : null) ?? [], null, 2),
    10,
    '',
    true,
  );

  // ── Admin notes ──
  const notes = textArea(
    selected ? selected.notes : null,
    3,
    'Internal notes about this configuration version (not visible to app users)…',
  );

  function collectDto(): ConfigVersionInput {
    let messages: ConfigMessage[];
    const raw = messagesJson.value.trim();
    try {
      const parsed = raw ? JSON.parse(raw) : [];
      if (!Array.isArray(parsed)) {
        throw new Error('Announcements JSON must be an array.');
      }
      messages = parsed as ConfigMessage[];
    } catch (err) {
      throw new MessagesJsonError(
        err instanceof Error && err.message.includes('array')
          ? err.message
          : `Announcements JSON is invalid: ${err instanceof Error ? err.message : String(err)}`,
      );
    }

    return {
      checkIDPro: checkIDPro.input.checked,
      useCheckIDVerifierToken: useCheckIDVerifierToken.input.checked,
      useMDLDataRequestEnabled: useMDLDataRequestEnabled.input.checked,
      mdlDataRequestGrantedElementsEnabled: mdlDataRequestGrantedElementsEnabled.input.checked,
      passportScanEnabled: passportScanEnabled.input.checked,
      mobileIDEnabled: mobileIDEnabled.input.checked,
      scanHistoryEnabled: scanHistoryEnabled.input.checked,
      businessSubscriptionEnabled: businessSubscriptionEnabled.input.checked,
      teamsWebPurchaseEnabled: teamsWebPurchaseEnabled.input.checked,
      teamsWebPurchaseLinkEnabled: teamsWebPurchaseLinkEnabled.input.checked,
      appleSandboxPurchasesOpen: appleSandboxPurchasesOpen.input.checked,
      ...duoSwitchPayload({
        duoCameraDirectionEnabled: duoCameraDirectionEnabled.input.checked,
        duoOuterDisplayEnabled: duoOuterDisplayEnabled.input.checked,
      }),
      ...navContinuitySwitchPayload({ navContinuityEnabled: navContinuityEnabled.input.checked }),
      analyticsEnabled: analyticsEnabled.input.checked,
      showOnboardingEnabled: showOnboardingEnabled.input.checked,
      maxFreeDailyScans: numOrNull(maxFreeDailyScans),
      maxFreeGuests: numOrNull(maxFreeGuests),
      passportCertListVersion: strOrNull(certVersion),
      passportCertListURL: strOrNull(certURL),
      passportCertListSHA256: strOrNull(certSHA256),
      passportCertListSizeBytes: numOrNull(certSizeBytes),
      passportCertListPublishedAt: dateOrNull(certPublishedAt),
      passportCertListReleaseNotes: strOrNull(certReleaseNotes),
      maintenanceEnabled: maintenanceEnabled.input.checked,
      maintenanceMessage: strOrNull(maintenanceMessage),
      maintenanceEndTime: strOrNull(maintenanceEndTime),
      maintenanceStatusURL: strOrNull(maintenanceStatusURL),
      minimumVersionRequired: strOrNull(minimumVersionRequired),
      minimumVersionRecommended: strOrNull(minimumVersionRecommended),
      minimumVersionRequiredMessage: strOrNull(minimumVersionRequiredMessage),
      minimumVersionRecommendedMessage: strOrNull(minimumVersionRecommendedMessage),
      appStoreURL: strOrNull(appStoreURL),
      surveyURL: strOrNull(surveyURL),
      messages,
      notes: strOrNull(notes),
    };
  }

  const saveErrorBanner = el('div', { class: 'banner banner-error', role: 'alert', hidden: true });
  function trySave(mode: 'update' | 'create'): void {
    saveErrorBanner.hidden = true;
    try {
      handlers.onSave(collectDto(), mode);
    } catch (err) {
      saveErrorBanner.textContent =
        err instanceof MessagesJsonError
          ? err.message
          : err instanceof Error
            ? err.message
            : 'Could not read the form.';
      saveErrorBanner.hidden = false;
    }
  }

  const cancelBtn = el(
    'button',
    { class: 'btn btn-ghost', type: 'button', onClick: handlers.onCancel },
    ['Cancel'],
  );
  const saveAsNewBtn = isNew
    ? null
    : el(
        'button',
        { class: 'btn btn-ghost', type: 'button', onClick: () => trySave('create') },
        ['Save as new version'],
      );
  const saveBtn = el(
    'button',
    {
      class: 'btn btn-primary',
      type: 'button',
      onClick: () => trySave(isNew ? 'create' : 'update'),
    },
    [isNew ? 'Create version' : 'Save changes'],
  );

  const title = isNew ? 'New configuration version' : `Editing v${selected?.version}`;
  const subtitle =
    !isNew && selected
      ? `Created ${fmtDate(selected.createdAt)}${
          selected.createdByEmail ? ` by ${selected.createdByEmail}` : ''
        }`
      : undefined;

  return el('section', { class: 'panel panel-wide' }, [
    el('div', { class: 'panel-head' }, [
      el('div', {}, [
        el('h2', { class: 'panel-title' }, [title]),
        subtitle ? el('p', { class: 'panel-sub', textContent: subtitle }) : null,
      ]),
      el(
        'div',
        { class: 'panel-actions' },
        [cancelBtn, saveAsNewBtn, saveBtn].filter(present),
      ),
    ]),
    el('div', { class: 'panel-body' }, [
      saveErrorBanner,

      groupHead('Feature flags'),
      fieldGrid([
        passportScanEnabled.element,
        mobileIDEnabled.element,
        scanHistoryEnabled.element,
        businessSubscriptionEnabled.element,
        teamsWebPurchaseEnabled.element,
        teamsWebPurchaseLinkEnabled.element,
        appleSandboxPurchasesOpen.element,
        duoCameraDirectionEnabled.element,
        duoOuterDisplayEnabled.element,
        navContinuityEnabled.element,
        analyticsEnabled.element,
        showOnboardingEnabled.element,
        checkIDPro.element,
        useCheckIDVerifierToken.element,
        useMDLDataRequestEnabled.element,
        mdlDataRequestGrantedElementsEnabled.element,
      ]),

      groupHead('Limits'),
      fieldGrid([
        field('Max free daily scans', maxFreeDailyScans),
        field('Max free guests', maxFreeGuests),
      ]),

      groupHead(
        'Passport certificate list (CSCA)',
        'Country Signing CA certificates used for passport chip verification. Updated quarterly.',
      ),
      fieldGrid([field('Version', certVersion), field('File size (bytes)', certSizeBytes)]),
      field('Download URL', certURL),
      field('SHA-256 hash', certSHA256, 'Used to verify file integrity after download.'),
      fieldGrid([field('Published at', certPublishedAt)]),
      field('Release notes', certReleaseNotes),

      groupHead('Maintenance'),
      maintenanceEnabled.element,
      field('Maintenance message', maintenanceMessage),
      field('Estimated end time', maintenanceEndTime, 'ISO 8601 format, e.g. 2026-01-20T14:00:00Z.'),
      field('Status page URL', maintenanceStatusURL),

      groupHead('Update gates'),
      fieldGrid([
        field(
          'Required version (force update)',
          minimumVersionRequired,
          'Users below this version are blocked from using the app.',
        ),
        field(
          'Recommended version (soft update)',
          minimumVersionRecommended,
          'Users below this version see a dismissible prompt.',
        ),
      ]),
      fieldGrid([
        field('Force update message', minimumVersionRequiredMessage),
        field('Soft update message', minimumVersionRecommendedMessage),
      ]),
      fieldGrid([
        field('App Store URL', appStoreURL, 'Used for both force and soft update prompts.'),
        field('Survey URL', surveyURL),
      ]),

      groupHead('Announcements'),
      el('p', { class: 'panel-note' }, [
        'JSON array of messages. Each item: { id, type, title, body?, actionText?, ' +
          "actionURL?, startDate?, endDate?, audience? }. type is one of announcement/info/" +
          "warning/alert; audience is one of all/free/pro/business.",
      ]),
      messagesJson,

      groupHead('Admin notes'),
      notes,
    ]),
  ]);
}

function groupHead(title: string, note?: string): HTMLElement {
  return el('div', {}, [
    el('div', { class: 'panel-subhead', textContent: title }),
    note ? el('p', { class: 'panel-note', textContent: note }) : null,
  ]);
}

function field(label: string, control: HTMLElement, hint?: string): HTMLElement {
  return el('label', { style: 'display:flex;flex-direction:column;gap:4px;' }, [
    el('span', {
      style: 'font-size:12px;font-weight:600;color:var(--text-muted);',
      textContent: label,
    }),
    control,
    hint ? el('span', { style: 'font-size:11px;color:var(--text-muted);', textContent: hint }) : null,
  ]);
}

function fieldGrid(children: (HTMLElement | null | false)[]): HTMLElement {
  return el(
    'div',
    { style: 'display:grid;grid-template-columns:repeat(auto-fit,minmax(220px,1fr));gap:12px;' },
    children.filter(present),
  );
}

function checkboxField(
  label: string,
  checked: boolean,
  hint?: string,
): { element: HTMLElement; input: HTMLInputElement } {
  const input = el('input', { type: 'checkbox', checked }) as HTMLInputElement;
  const element = el(
    'label',
    { style: 'display:flex;align-items:flex-start;gap:8px;font-size:13px;cursor:pointer;' },
    [
      input,
      el('div', {}, [
        el('div', { style: 'font-weight:600;', textContent: label }),
        hint ? el('div', { style: 'font-size:11px;color:var(--text-muted);', textContent: hint }) : null,
      ]),
    ],
  );
  return { element, input };
}

function textInput(value: string | null, placeholder = ''): HTMLInputElement {
  return el('input', {
    class: 'control',
    type: 'text',
    value: value ?? '',
    placeholder,
    style: 'width:100%;',
  }) as HTMLInputElement;
}

function numberInput(value: number | null, placeholder = ''): HTMLInputElement {
  return el('input', {
    class: 'control',
    type: 'number',
    min: '0',
    value: value === null || value === undefined ? '' : String(value),
    placeholder,
    style: 'width:100%;',
  }) as HTMLInputElement;
}

function dateInput(value: string | null): HTMLInputElement {
  return el('input', {
    class: 'control',
    type: 'date',
    value: toDateInputValue(value),
    style: 'width:100%;',
  }) as HTMLInputElement;
}

function textArea(
  value: string | null,
  rows = 3,
  placeholder = '',
  monospace = false,
): HTMLTextAreaElement {
  return el('textarea', {
    class: monospace ? 'token-input' : 'control',
    rows,
    placeholder,
    style: 'width:100%;resize:vertical;',
    textContent: value ?? '',
  }) as HTMLTextAreaElement;
}

function strOrNull(input: HTMLInputElement | HTMLTextAreaElement): string | null {
  const v = input.value.trim();
  return v ? v : null;
}

function numOrNull(input: HTMLInputElement): number | null {
  const v = input.value.trim();
  if (!v) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function dateOrNull(input: HTMLInputElement): string | null {
  const v = input.value.trim();
  if (!v) return null;
  return v.length === 10 ? `${v}T00:00:00.000Z` : v;
}

function toDateInputValue(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toISOString().slice(0, 10);
}
